/**
 * People-attribution rework S6 (plan 2026-08-29, D-A/D-D/D-E/D-F/D-I/D-J):
 * the Traffic sources view — the SAME served campaign / creative rows the
 * cards read, shaped for the panel. PURE: no React, no arithmetic on people
 * (AC-056) — every people figure, cost and CAC is read from the payload;
 * the total row is the SERVED scope block, never a sum of rows (R5).
 */
import type { FunnelUnit } from './funnelUnit';
import { metaPurchasesCopy, type MetaPurchasesCopy } from './metaPurchasesCopy';
import type {
  FunnelCampaignEconomicsRow,
  FunnelCreativePerformanceRow,
  FunnelCreativePerformanceSection,
  FunnelReportsDashboardData,
} from './types';

export type TrafficSourcesView = 'campaigns' | 'creatives';

export const TRAFFIC_SOURCES_URL_PARAM = 'traffic';
/**
 * S13 wave D (§17.1 item 11): the selected campaign (Meta campaign id) rides
 * beside `traffic` — on the Creatives tab it FILTERS the rows to that
 * campaign, on the Campaigns tab it HIGHLIGHTS the row (never filters: the
 * served total keeps its context). Absent = nothing selected; the page writes
 * the key only for a non-empty value.
 */
export const TRAFFIC_CAMPAIGN_URL_PARAM = 'campaign';
export function parseTrafficCampaignParam(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export function parseTrafficSourcesParam(value: string | null | undefined): TrafficSourcesView | null {
  return value === 'campaigns' || value === 'creatives' ? value : null;
}

/** A gate cell: available (rate + evidence pair) or a typed absence with a reason. */
export interface TrafficGateView {
  available: boolean;
  rate?: number | null;
  numerator?: number;
  denominator?: number;
  /** Why the cell is n/a — rendered verbatim (typed absence, never a zero). */
  reason?: string;
}

export interface TrafficMoneyView {
  /** null = a real «—» (no spend / no ad account); undefined = typed absence. */
  value: number | null | undefined;
  reason?: string;
}

export interface TrafficSourceRow {
  kind: 'campaign' | 'creative';
  id: string;
  label: string;
  sublabel?: string;
  /** D-I: the one «No campaign» row (direct, missing UTM, native SDK traffic). */
  isNoCampaign: boolean;
  /** D-E (a): UTM sources with no provider entity — people and gates, no spend/CAC. */
  identityPlane: 'linkage' | 'behavioural' | undefined;
  spend: TrafficMoneyView;
  people: { value: number | undefined; reason?: string };
  firstToSecond: TrafficGateView;
  paywall: TrafficGateView;
  purchase: TrafficGateView;
  costPerPerson: TrafficMoneyView;
  cac: TrafficMoneyView & { count?: number };
  meta: MetaPurchasesCopy | undefined;
  /** Creative rows: the parent campaign (name or id). */
  campaignLabel?: string;
}

export interface TrafficTotalRow {
  label: string;
  spend: TrafficMoneyView;
  people: number;
  firstToSecond: TrafficGateView;
  paywall: TrafficGateView;
  purchase: TrafficGateView;
  costPerPerson: TrafficMoneyView;
  cac: TrafficMoneyView & { count?: number };
  overlap: { count: number; noun: string };
}

export const PEOPLE_REFRESH_REASON = 'people data needs refresh';
const NO_AD_ACCOUNT_REASON = 'no ad account';
const NO_PAID_REASON = 'no paid conversions yet';
const NO_VISIT_TWIN_REASON = 'no visit twin for this gate';

const gate = (numerator: number | undefined, denominator: number | undefined, reason = PEOPLE_REFRESH_REASON): TrafficGateView => (
  typeof numerator === 'number' && typeof denominator === 'number'
    ? { available: true, rate: denominator > 0 ? numerator / denominator : null, numerator, denominator }
    : { available: false, reason }
);

const cacView = (spend: number | null | undefined, cac: number | null | undefined, newPaying: number | undefined, hasAccount: boolean): TrafficMoneyView & { count?: number } => {
  if (!hasAccount) return { value: null, reason: NO_AD_ACCOUNT_REASON };
  if (cac === undefined) return { value: undefined, reason: PEOPLE_REFRESH_REASON };
  if (cac === null) {
    if (typeof newPaying === 'number' && newPaying === 0) return { value: null, reason: NO_PAID_REASON, count: 0 };
    if (spend === null) return { value: null, reason: 'spend withheld (mixed currency)' };
    return { value: null, reason: 'no spend on this row' };
  }
  return { value: cac, count: newPaying };
};

/**
 * Identity layer (QW1, S8 review): the reader-facing name leads, ids demote to
 * the caption. The served `metaCampaignName` is the Meta campaign name; the
 * behavioural `campaign` (utm_campaign) stays the row key and the fallback.
 */
export function campaignLabel(row: FunnelCampaignEconomicsRow): string {
  if (row.campaign === '(not set)') return 'No campaign';
  return row.metaCampaignName?.trim() || row.campaign;
}

export function campaignSublabel(row: FunnelCampaignEconomicsRow): string | undefined {
  if (row.campaign === '(not set)') return 'direct, missing UTM, native SDK traffic';
  const metaId = row.metaCampaignId && row.metaCampaignId !== row.campaign ? `Meta ${row.metaCampaignId}` : undefined;
  if (row.metaCampaignName?.trim()) return [row.campaign, metaId].filter(Boolean).join(' · ');
  return metaId;
}

/** D-D: only campaigns that brought sessions INTO our funnels; unmatched delivery rows are absent. */
export function isFunnelCampaignRow(row: FunnelCampaignEconomicsRow): boolean {
  return (row.sessions ?? 0) > 0 || (row.startedJourneys ?? 0) > 0;
}

export function buildTrafficCampaignRows(data: FunnelReportsDashboardData, unit: FunnelUnit): TrafficSourceRow[] {
  const rows = (data.campaignEconomics ?? []).filter(isFunnelCampaignRow);
  const mapped = rows.map((row): TrafficSourceRow => {
    const isNoCampaign = row.campaign === '(not set)';
    const hasAccount = !isNoCampaign && row.peopleIdentityPlane !== 'behavioural' && Boolean(row.metaCampaignId);
    const spend: TrafficMoneyView = isNoCampaign || !hasAccount
      ? { value: null, reason: isNoCampaign ? 'no campaign' : NO_AD_ACCOUNT_REASON }
      : { value: row.spend ?? null, reason: row.spend === null ? 'spend withheld (mixed currency)' : undefined };
    if (unit === 'people') {
      return {
        kind: 'campaign',
        id: row.campaign,
        label: campaignLabel(row),
        sublabel: campaignSublabel(row),
        isNoCampaign,
        identityPlane: row.peopleIdentityPlane,
        spend,
        people: typeof row.startedPeople === 'number' ? { value: row.startedPeople } : { value: undefined, reason: PEOPLE_REFRESH_REASON },
        firstToSecond: gate(row.firstToSecondPeople, row.firstScreenOpenedPeople),
        paywall: gate(row.paywallReachedPeople, row.startedPeople),
        purchase: gate(row.purchasePeople, row.startedPeople),
        costPerPerson: !hasAccount
          ? { value: null, reason: spend.reason }
          : { value: row.costPerStartPeople, reason: row.costPerStartPeople === undefined ? PEOPLE_REFRESH_REASON : undefined },
        cac: cacView(row.spend, row.cac, row.newPayingPeople, hasAccount),
        meta: hasAccount ? metaPurchasesCopy(row) : undefined,
      };
    }
    // Visits: journeys — the campaign lane serves no paywall journey twin.
    return {
      kind: 'campaign',
      id: row.campaign,
      label: campaignLabel(row),
      sublabel: campaignSublabel(row),
      isNoCampaign,
      identityPlane: row.peopleIdentityPlane,
      spend,
      people: { value: row.startedJourneys },
      firstToSecond: gate(row.firstToSecondJourneys, row.firstScreenOpenedJourneys, NO_VISIT_TWIN_REASON),
      paywall: { available: false, reason: NO_VISIT_TWIN_REASON },
      purchase: gate(row.purchaseJourneys, row.startedJourneys, NO_VISIT_TWIN_REASON),
      costPerPerson: !hasAccount
        ? { value: null, reason: spend.reason }
        : { value: row.costPerFirstScreenOpen ?? null, reason: 'cost per first screen (visits)' },
      cac: cacView(row.spend, row.cac, row.newPayingPeople, hasAccount),
      meta: hasAccount ? metaPurchasesCopy(row) : undefined,
    };
  });
  // Sorted by spend (D-I: the «No campaign» row rides last among the unspent).
  return mapped.sort((left, right) => (right.spend.value ?? -1) - (left.spend.value ?? -1));
}

/** AC-052: the total row is the SERVED scope block — never Σ rows. */
export function buildTrafficTotalRow(data: FunnelReportsDashboardData, unit: FunnelUnit): TrafficTotalRow | undefined {
  const scope = data.campaignEconomicsPeople;
  if (!scope || unit !== 'people') return undefined;
  const spendTotal = data.paidSummaryTiles?.totals.spend;
  const spend: TrafficMoneyView = typeof spendTotal === 'number'
    ? { value: spendTotal }
    : { value: undefined, reason: 'spend total not served on this payload' };
  return {
    label: 'All sources (de-duplicated)',
    spend,
    people: scope.startedPeople,
    firstToSecond: gate(scope.firstToSecondPeople, scope.firstScreenOpenedPeople),
    paywall: gate(scope.paywallReachedPeople, scope.startedPeople),
    purchase: gate(scope.purchasePeople, scope.startedPeople),
    // Scope-level unit costs are not served (spend ÷ de-duplicated people
    // would be client arithmetic on money) — typed absence, never derived.
    costPerPerson: { value: undefined, reason: 'scope cost per person is not served' },
    cac: { value: undefined, reason: 'scope CAC is not served', count: scope.newPayingPeople },
    overlap: { count: scope.peopleOnMultipleCampaigns, noun: 'campaign' },
  };
}

export function buildTrafficCreativeRows(section: FunnelCreativePerformanceSection | undefined, unit: FunnelUnit): TrafficSourceRow[] {
  const rows = section?.rows ?? [];
  return rows.map((creative: FunnelCreativePerformanceRow): TrafficSourceRow => {
    const id = creative.adId ?? creative.creativeId ?? 'unknown';
    const label = creative.creativeName || creative.creativeTitle || creative.creativeId || creative.adId || 'n/a';
    const spend: TrafficMoneyView = { value: creative.adSpend ?? creative.spend ?? null };
    const linked = creative.peopleIdentityPlane === 'linkage';
    if (unit === 'people') {
      return {
        kind: 'creative',
        id,
        label,
        sublabel: `ad ${creative.adId ?? 'n/a'}`,
        isNoCampaign: false,
        identityPlane: creative.peopleIdentityPlane,
        spend,
        people: linked && typeof creative.startedPeople === 'number'
          ? { value: creative.startedPeople }
          : { value: undefined, reason: linked ? PEOPLE_REFRESH_REASON : 'no journey linked to this ad' },
        firstToSecond: gate(creative.firstToSecondPeople, creative.firstScreenOpenedPeople, linked ? PEOPLE_REFRESH_REASON : 'no journey linked to this ad'),
        paywall: gate(creative.paywallReachedPeople, creative.startedPeople, linked ? PEOPLE_REFRESH_REASON : 'no journey linked to this ad'),
        purchase: gate(creative.purchasePeople, creative.startedPeople, linked ? PEOPLE_REFRESH_REASON : 'no journey linked to this ad'),
        costPerPerson: { value: creative.costPerStartPeople, reason: creative.costPerStartPeople === undefined ? (linked ? PEOPLE_REFRESH_REASON : 'no journey linked to this ad') : undefined },
        cac: cacView(creative.adSpend ?? creative.spend ?? null, creative.cac, creative.newPayingPeople, true),
        meta: metaPurchasesCopy(creative),
        campaignLabel: creative.campaignName ?? creative.campaignId,
      };
    }
    return {
      kind: 'creative',
      id,
      label,
      sublabel: `ad ${creative.adId ?? 'n/a'}`,
      isNoCampaign: false,
      identityPlane: creative.peopleIdentityPlane,
      spend,
      people: typeof creative.adSessions === 'number' ? { value: creative.adSessions } : { value: undefined, reason: 'sessions did not resolve this ad' },
      firstToSecond: { available: false, reason: NO_VISIT_TWIN_REASON },
      paywall: { available: false, reason: NO_VISIT_TWIN_REASON },
      purchase: gate(creative.adPurchaseJourneys, creative.adSessions, 'sessions did not resolve this ad'),
      costPerPerson: { value: creative.adCostPerOffer ?? null, reason: 'cost per offer (visits)' },
      cac: cacView(creative.adSpend ?? creative.spend ?? null, creative.cac, creative.newPayingPeople, true),
      meta: metaPurchasesCopy(creative),
      campaignLabel: creative.campaignName ?? creative.campaignId,
    };
  });
}
